import React, { useEffect, useState } from "react";
import { getCardPriceHistory } from "../../services/pokemonAPI";
import {
  buildChartSeries,
  segmentPath,
  historyHeadline,
  priceChange,
} from "../../priceHistoryChart";

const WIDTH = 320;
const HEIGHT = 140;

// History points are UTC day buckets (priceHistoryService startOfUtcDay), so
// they are labelled in UTC: local getters showed them a day early west of
// UTC. (Code review LOCALE-10)
const fmtDate = (value) =>
  new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Full date for one observation, e.g. "4 Aug 2026".
 *
 * Written out rather than delegated to toLocaleDateString because this string
 * is the app's claim about when the price was taken: it has to read the same on
 * every machine, and a guard test has to be able to assert the exact text.
 */
export const observedOn = (value) => {
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** A count that is a non-negative integer, or 0 — never NaN in the copy. */
const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * The count phrase shared by the headline and the provenance line.
 *
 * `plotted` is what is on the line; `omitted` is what the server holds for this
 * window but deliberately did not plot (P1-09: another currency, another
 * source). When something was omitted the phrase names BOTH numbers, because
 * "3 observations" beside a graph the server built from 3 of 6 rows reads as
 * "this window holds 3 observations" — which is false, and false in the
 * flattering direction: it makes a thin line look like the whole story.
 */
const observationCount = (plotted, omitted) =>
  omitted > 0
    ? `${plotted} of ${plotted + omitted} observations`
    : `${plotted} observation${plotted === 1 ? "" : "s"}`;

/** "USD", "USD and EUR", "USD, EUR and GBP" */
const inWords = (items) => {
  if (items.length <= 1) return items[0] || "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

/**
 * The provenance line under the graph, built from the observations themselves.
 *
 * This used to read "daily market snapshots", which was a cadence nothing
 * enforced — the refresh script had no schedule at all, so "daily" meant
 * "whenever somebody remembered to run it" (#P1-08). A refresh IS scheduled
 * now, but the honest thing to print is still the date we actually have rather
 * than the cadence we intend: a cron that fails for a week must not keep the
 * screen claiming a week of samples.
 *
 * `omittedObservations` is the server's count of rows in this window that it
 * would not put on this line (P1-09). Passing it makes the count phrase read
 * "3 of 6" instead of a bare "3": the graph is a sample of the window, and the
 * sentence has to say so.
 *
 * Exported so the honest-copy guard has something to point at, and so the three
 * shapes that matter — none, one, several — are testable without a DOM.
 */
export function priceProvenance({ points = [], days = 90, omittedObservations = 0 } = {}) {
  const valid = (Array.isArray(points) ? points : []).filter(
    (p) => p && observedOn(p.observedAt)
  );

  if (valid.length === 0) return "No price observations recorded for this card.";

  const last = valid[valid.length - 1];
  const count = `${observationCount(valid.length, toCount(omittedObservations))} in the last ${days} days`;
  const line = [last.source, last.currency, `last sampled ${observedOn(last.observedAt)}`, count]
    .filter(Boolean)
    .join(" · ");

  // One dot cannot have a gap in it, so only say so when a gap is drawable.
  return valid.length > 1 ? `${line}. Gaps are days we didn’t sample.` : `${line}.`;
}

/**
 * Header copy for the graph, counting what was PLOTTED against what the window
 * actually held.
 *
 * Delegates to `historyHeadline` whenever nothing was omitted, so the ordinary
 * single-currency card keeps exactly the copy it had and there is one source of
 * truth for it.
 */
export function chartHeadline({ points = [], days = 90, omittedObservations = 0 } = {}) {
  const plotted = Array.isArray(points) ? points.length : 0;
  const omitted = toCount(omittedObservations);
  if (plotted === 0 || omitted === 0) return historyHeadline({ points, days });

  const label = `${observationCount(plotted, omitted)} over ${days} days`;
  // One point is still not a direction, however many rows sit behind it.
  return plotted === 1 ? `${label} · not enough history to show a trend` : label;
}

/**
 * What the graph is NOT showing, said out loud — or null when it is showing
 * everything.
 *
 * P1-09 made the server read price history in ONE currency from ONE source: a
 * 90-day window that straddles a provider-currency flip no longer draws USD
 * observations straight into EUR ones and prints the difference as a price
 * move. The rows it steps over are still real, and the payload reports them
 * (`omittedObservations`, `mixedCurrency`/`currencies`, `mixedSource`/`sources`)
 * precisely so this screen can say they exist. Rendering the shortened series
 * without this sentence would swap one lie for a quieter one: a confident line
 * through a handful of points, presenting itself as the whole window.
 *
 * No conversion is offered, and none is implied — there is no approved FX
 * source (#64).
 */
export function unplottedNotice({
  points = [],
  days = 90,
  omittedObservations = 0,
  currency = null,
  source = null,
  mixedCurrency = false,
  currencies = [],
  mixedSource = false,
  sources = [],
} = {}) {
  const omitted = toCount(omittedObservations);
  const others = (list, mine) =>
    (Array.isArray(list) ? list : []).filter((v) => v && v !== mine);
  const otherCurrencies = others(currencies, currency);
  const otherSources = others(sources, source);

  const parts = [];

  if (omitted > 0) {
    const plotted = Array.isArray(points) ? points.length : 0;
    parts.push(`Plotting ${observationCount(plotted, omitted)} in the last ${days} days.`);
  }

  if (mixedCurrency && otherCurrencies.length > 0) {
    parts.push(
      `This window also holds prices in ${inWords(otherCurrencies)}` +
        `${currency ? `, and this line is ${currency}` : ""}. ` +
        `Those aren’t plotted and aren’t converted — there is no approved FX source.`
    );
  }

  if (mixedSource && otherSources.length > 0) {
    parts.push(
      `It also holds prices from ${inWords(otherSources)}` +
        `${source ? `, and this line is ${source}` : ""}. ` +
        `Two feeds are two markets, so they aren’t drawn as one line.`
    );
  }

  return parts.length > 0 ? parts.join(" ") : null;
}

/**
 * PriceHistoryChart — what this card's price has actually been doing.
 *
 * Drawn by hand rather than with a charting library for one reason: a library
 * interpolates straight through days we never sampled. The refresh runs on a
 * schedule but skips cards that are not yet due (high value is refreshed far
 * more often than mid value), so those holes are real and are drawn as GAPS —
 * separate line segments — never a smooth line through invented prices.
 *
 * Every figure is labelled with the source and the currency that source quoted.
 * Nothing is converted: there is no approved FX source (#64).
 */
const PriceHistoryChart = ({ cardId, condition = "near_mint", days = 90 }) => {
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!cardId) return undefined;
    let cancelled = false;
    setState({ status: "loading" });
    getCardPriceHistory(cardId, { condition, days })
      .then((data) => {
        if (!cancelled) setState({ status: "done", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: typeof err === "string" ? err : err?.message || "Could not load price history",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cardId, condition, days]);

  if (state.status === "loading") {
    return (
      <div data-testid="price-history-loading" style={{ padding: 16, fontSize: 13, color: "#64748b" }}>
        Loading price history…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div data-testid="price-history-error" style={{ padding: 16, fontSize: 13, color: "#b91c1c" }}>
        {state.message}
      </div>
    );
  }

  const data = state.data || {};
  const points = data.points || [];

  // Bulk is a deliberate policy, not missing data — say which it is.
  if (data.tracked === false && data.reason === "bulk") {
    return (
      <div data-testid="price-history-bulk" style={{ padding: 16, fontSize: 13, color: "#64748b" }}>
        <strong style={{ color: "#0f172a" }}>Bulk card.</strong> Cards under the bulk
        threshold aren’t priced individually — they still count toward a trade at
        the standard bulk rate.
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div data-testid="price-history-empty" style={{ padding: 16, fontSize: 13, color: "#64748b" }}>
        No price observations yet. This card is tracked — the next refresh will
        record its first point.
      </div>
    );
  }

  const series = buildChartSeries(points, { width: WIDTH, height: HEIGHT });
  const change = priceChange(points);
  const last = points[points.length - 1];
  const changeColor =
    change.direction === "up" ? "#16a34a" : change.direction === "down" ? "#dc2626" : "#64748b";

  // P1-09 reports what the window held beyond this line. `data` is spread in so
  // the caveat picks up every signal the payload carries without this component
  // having to restate the field list twice.
  const headline = chartHeadline({ ...data, points, days });
  const unplotted = unplottedNotice({ ...data, points, days });

  return (
    <div data-testid="price-history-chart" style={{ padding: "12px 16px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
          {last.price.toFixed(2)} {last.currency}
        </span>
        {change.pct != null && (
          <span data-testid="price-history-change" style={{ fontSize: 13, fontWeight: 800, color: changeColor }}>
            {change.abs > 0 ? "+" : ""}
            {change.abs.toFixed(2)} ({change.pct > 0 ? "+" : ""}
            {change.pct.toFixed(1)}%)
          </span>
        )}
      </div>

      <div data-testid="price-history-headline" style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
        {headline}
      </div>

      {/* Shown BEFORE the graph, because it changes how the graph should be
          read: a line built from part of the window is not the window. */}
      {unplotted && (
        <div
          data-testid="price-history-unplotted"
          role="note"
          style={{
            fontSize: 11,
            lineHeight: 1.45,
            color: "#92400e",
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 6,
            padding: "6px 8px",
            marginTop: 8,
          }}
        >
          {unplotted}
        </div>
      )}

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        role="img"
        aria-label={`Price history: ${headline}`}
        style={{ marginTop: 10, display: "block", overflow: "visible" }}
      >
        {series.segments.map((seg, i) => (
          <g key={i}>
            {/* A run of two or more observations is a line; a lone observation is
                a dot, because one point is not a trend. */}
            {seg.length > 1 && (
              <path
                data-testid="price-history-segment"
                d={segmentPath(seg)}
                fill="none"
                stroke="#6b21a8"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {seg.map((p, j) => (
              <circle key={j} cx={p.x} cy={p.y} r={seg.length === 1 ? 3.5 : 2.5} fill="#6b21a8" />
            ))}
          </g>
        ))}
      </svg>

      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b" }}>
        <span>{fmtDate(points[0].observedAt)}</span>
        <span>{fmtDate(last.observedAt)}</span>
      </div>

      {/* Says what the number IS (a dated snapshot) rather than what it is not.
          The honestCopy guard (#64) forbids planting the word "live" next to
          pricing at all — even to deny it — and now forbids a hardcoded cadence
          too, which is why this sentence is computed from the observations. */}
      <div data-testid="price-history-provenance" style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
        {priceProvenance({ points, days, omittedObservations: data.omittedObservations })}
      </div>
    </div>
  );
};

export default PriceHistoryChart;
